import React, { useState, useEffect } from "react";
import { Plus, Trash2, Play, Loader2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import pb from "../../lib/pocketbaseClient";

// Helper to extract YouTube video ID
const getYouTubeId = (url) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2]?.length === 11 ? match[2] : null;
};

// Helper to get thumbnail URL
const getThumb = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

// Helper to get PocketBase file URL for thumbnail (if stored)
const getThumbnailUrl = (video) => {
  if (video.thumbnailFile) {
    return pb.files.getURL(video, video.thumbnailFile);
  }
  return getThumb(video.videoId);
};

const VideoManagement = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const records = await pb.collection("videos").getFullList({
        sort: "-created",
        $autoCancel: false,
      });
      setVideos(records);
    } catch (error) {
      console.error("Failed to fetch videos:", error);
      if (error.status === 404) {
        console.log("Videos collection not found");
        setVideos([]);
      } else {
        toast.error("Failed to load videos");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!title.trim()) {
      toast.error("Please enter a video title");
      return;
    }
    if (!url.trim()) {
      toast.error("Please enter a YouTube URL");
      return;
    }

    const videoId = getYouTubeId(url);
    if (!videoId) {
      toast.error("Invalid YouTube URL");
      return;
    }

    setAdding(true);
    try {
      const newVideo = {
        title: title.trim(),
        youtubeUrl: url.trim(),
        videoId: videoId,
        status: "active",
      };

      await pb.collection("videos").create(newVideo);
      toast.success("Video added successfully");
      setTitle("");
      setUrl("");
      fetchVideos();
    } catch (error) {
      console.error("Add error:", error);
      toast.error(error.message || "Failed to add video");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await pb.collection("videos").delete(deleteConfirm.id);
      toast.success("Video deleted successfully");
      fetchVideos();
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete video");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Video Management</h1>
          <p className="text-slate-500 text-sm">Manage YouTube videos for the video gallery</p>
        </div>
        <button
          onClick={fetchVideos}
          className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <p className="text-2xl font-bold text-slate-800">{videos.length}</p>
          <p className="text-sm text-slate-500">Total Videos</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <p className="text-2xl font-bold text-green-600">{videos.filter(v => v.status === 'active').length}</p>
          <p className="text-sm text-slate-500">Active</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <p className="text-2xl font-bold text-amber-600">{videos.filter(v => v.status === 'inactive').length}</p>
          <p className="text-sm text-slate-500">Inactive</p>
        </div>
      </div>

      {/* Add Form */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
        <h2 className="text-lg font-semibold mb-4">Add New Video</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Video Title *</label>
            <input
              type="text"
              placeholder="Enter video title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">YouTube URL *</label>
            <input
              type="text"
              placeholder="https://youtu.be/... or https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>
        <button
          onClick={handleAdd}
          disabled={adding || !title.trim() || !url.trim()}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus size={18} />}
          Add Video
        </button>
      </div>

      {/* Video List */}
      {videos.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-xl">
          <Play className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p>No videos yet. Add your first video above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {videos.map((video) => (
            <div
              key={video.id}
              className="bg-white rounded-xl overflow-hidden shadow-md border border-slate-200 hover:shadow-lg transition group"
            >
              <div className="relative aspect-video bg-slate-100 cursor-pointer" onClick={() => setPlayingVideo(video)}>
                <img
                  src={getThumbnailUrl(video)}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/480x360?text=No+Thumbnail";
                  }}
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                    <Play className="w-6 h-6 text-orange-500 fill-orange-500 ml-0.5" />
                  </div>
                </div>
                {video.status === 'inactive' && (
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    Inactive
                  </div>
                )}
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-slate-800 truncate">{video.title}</h3>
                <p className="text-xs text-slate-500 mt-1">
                  Added: {new Date(video.created).toLocaleDateString('en-IN')}
                </p>
                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => setDeleteConfirm(video)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Player Modal */}
      {playingVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
        >
          <div
            className="relative bg-black rounded-xl overflow-hidden w-full max-w-4xl aspect-video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPlayingVideo(null)}
              className="absolute top-2 right-2 p-2 bg-white/10 backdrop-blur rounded-full text-white hover:bg-white/20 z-10 transition"
            >
              <X className="w-5 h-5" />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${playingVideo.videoId}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
              title={playingVideo.title}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Delete Video?</h3>
            <p className="text-slate-500 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.title}</strong>?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoManagement;
